import useChatbot from '@/hooks/useChatbot';
import { mapTextToAttachment, useChatbotInput } from '@/hooks/useChatbotInput';
import useAnalytics from '@/lib/analytics/useAnalytics';
import { useChatbotStore } from '@/lib/chat/ChatbotStore';
import { api } from '@/lib/trpc/react';
import type { Annotation, HighlightRect } from '@anaralabs/lector';
import { useAnnotations } from '@anaralabs/lector';
import { getTZDate } from '@unriddle/common';
import cuid from 'cuid';
import { useAtom } from 'jotai';
import { useCallback } from 'react';
import {
  focusedAnnotationIdAtom,
  focusedHoverAnnotationIdAtom,
} from './pdf-content';

export type StoredAnnotation = {
  id: string;
  userId: string;
  createdAt: string;
  updatedAt: string;
  text: string;
  chatbotId: string;
  pageNumber: number;
  userDisplayName: string;
  highlights: HighlightRect[];
  comment: string | null;
  color: string;
  borderColor: string;
};

type CreateAnnotationInput = {
  id: string;
  chatbotId: string;
  text: string;
  pageNumber: number;
  highlights: HighlightRect[];
  color: string;
  borderColor: string;
  comment?: string;
  userId: string;
  userDisplayName: string;
};

// Helper to convert backend annotation to PDF layer annotation
export const convertToClientAnnotation = (
  annotation: StoredAnnotation,
): Annotation => ({
  id: annotation.id,
  pageNumber: annotation.pageNumber,
  highlights: annotation.highlights,
  color: annotation.color || '',
  borderColor: annotation.borderColor || '',
  comment: annotation.comment || '',
  createdAt: new Date(annotation.createdAt),
  updatedAt: new Date(annotation.updatedAt),
  metadata: {
    chatbotId: annotation.chatbotId,
    text: annotation.text,
    userId: annotation.userId,
    userDisplayName: annotation.userDisplayName,
  },
});

// Helper to convert PDF layer annotation to backend annotation
const convertToBackendAnnotation = (
  annotation: Partial<Annotation> & {
    pageNumber: number;
    highlights: HighlightRect[];
  },
  chatbotId: string,
  id: string,
): CreateAnnotationInput => ({
  id,
  chatbotId,
  pageNumber: annotation.pageNumber,
  highlights: annotation.highlights,
  color: annotation.color || '',
  borderColor: annotation.borderColor || '',
  comment: annotation.comment || '',
  text: (annotation.metadata?.text as string) || 'text',
  userId: annotation.metadata?.userId as string,
  userDisplayName: annotation.metadata?.userDisplayName as string,
});

export const useDocumentAnnotations = (chatbotId: string) => {
  const [focusedAnnotationId, setFocusedAnnotationId] = useAtom(
    focusedAnnotationIdAtom,
  );
  const [focusedHoverAnnotationId, setFocusedHoverAnnotationId] = useAtom(
    focusedHoverAnnotationIdAtom,
  );
  const { addAttachments } = useChatbotInput();
  const utils = api.useUtils();
  const { chatbot } = useChatbot({ id: chatbotId });
  const {
    addAnnotation: addClientAnnotation,
    updateAnnotation: updateClientAnnotation,
    deleteAnnotation: deleteClientAnnotation,
  } = useAnnotations();
  const { track } = useAnalytics();
  const toggle = useChatbotStore((state) => state.toggle);
  const { data: savedAnnotations, isLoading } =
    api.documentAnnotation.all.useQuery(chatbotId, {
      enabled: !!chatbotId && chatbot?.isIndexed,
    });

  const createMutation = api.documentAnnotation.create.useMutation({
    onSettled: () => {
      utils.documentAnnotation.all.invalidate(chatbotId);
    },
  });

  const updateMutation = api.documentAnnotation.update.useMutation({
    onSuccess: (updatedAnnotation) => {
      utils.documentAnnotation.all.setData(chatbotId, (oldData) => {
        if (!oldData) return { annotations: [updatedAnnotation] };
        return {
          annotations: oldData.annotations.map((ann) =>
            ann.id === updatedAnnotation.id ? updatedAnnotation : ann,
          ),
        };
      });
    },
    onError: (_, variables) => {
      const originalAnnotation = savedAnnotations?.annotations.find(
        (ann) => ann.id === variables.id,
      );
      if (originalAnnotation) {
        const clientAnnotation = convertToClientAnnotation({
          ...originalAnnotation,
          highlights: originalAnnotation.highlights as HighlightRect[],
        });
        updateClientAnnotation(variables.id, clientAnnotation);
      }
    },
  });

  const deleteMutation = api.documentAnnotation.delete.useMutation({
    onMutate: async (annotationId) => {
      // Cancel any outgoing refetches
      await utils.documentAnnotation.all.cancel(chatbotId);

      // Snapshot the previous value
      const previousData = utils.documentAnnotation.all.getData(chatbotId);

      // Optimistically remove from client state
      deleteClientAnnotation(annotationId);

      // Optimistically update the query data
      utils.documentAnnotation.all.setData(chatbotId, (oldData) => {
        if (!oldData) return { annotations: [] };
        return {
          annotations: oldData.annotations.filter(
            (ann) => ann.id !== annotationId,
          ),
        };
      });

      return { previousData };
    },
    onError: (_, annotationId, context) => {
      const originalAnnotation = context?.previousData?.annotations.find(
        (ann) => ann.id === annotationId,
      );
      if (originalAnnotation) {
        const clientAnnotation = convertToClientAnnotation({
          ...originalAnnotation,
          highlights: originalAnnotation.highlights as HighlightRect[],
        });
        addClientAnnotation(clientAnnotation);
      }

      utils.documentAnnotation.all.setData(chatbotId, context?.previousData);
    },
  });

  const handleCreateAnnotation = useCallback(
    (
      annotation: Partial<Annotation> & {
        pageNumber: number;
        highlights: HighlightRect[];
      },
    ) => {
      const newId = cuid();
      const baseAnnotation = convertToBackendAnnotation(
        annotation,
        chatbotId,
        newId,
      );

      const userId = annotation.metadata?.userId as string;
      const userDisplayName = annotation.metadata?.userDisplayName as string;

      const clientAnnotation: Annotation = {
        id: newId,
        pageNumber: annotation.pageNumber,
        highlights: annotation.highlights,
        color: annotation.color || '',
        borderColor: annotation.borderColor || '',
        comment: annotation.comment || '',
        createdAt: new Date(getTZDate()),
        updatedAt: new Date(getTZDate()),
        metadata: {
          chatbotId,
          text: annotation.metadata?.text || 'text',
          userId,
          userDisplayName,
        },
      };
      addClientAnnotation(clientAnnotation);

      track('annotation_created');
      utils.documentAnnotation.all.cancel(chatbotId);
      utils.documentAnnotation.all.setData(chatbotId, (oldData) => {
        const optimisticAnnotation = {
          ...baseAnnotation,
          userId,
          userDisplayName,
          createdAt: getTZDate(),
          updatedAt: getTZDate(),
          comment: baseAnnotation.comment || null,
        };

        if (!oldData) return { annotations: [optimisticAnnotation] };
        return {
          annotations: [...oldData.annotations, optimisticAnnotation],
        };
      });

      createMutation.mutate(baseAnnotation, {
        onError: () => {
          deleteClientAnnotation(newId);
          utils.documentAnnotation.all.setData(chatbotId, (oldData) => {
            if (!oldData) return { annotations: [] };
            return {
              annotations: oldData.annotations.filter(
                (ann) => ann.id !== newId,
              ),
            };
          });
        },
      });
    },
    [
      chatbotId,
      createMutation,
      addClientAnnotation,
      deleteClientAnnotation,
      utils,
    ],
  );

  const handleUpdateAnnotation = useCallback(
    (id: string, updates: Annotation) => {
      utils.documentAnnotation.all.cancel(chatbotId);

      const originalAnnotation = savedAnnotations?.annotations.find(
        (ann) => ann.id === id,
      );
      if (!originalAnnotation) return;

      const updatedText =
        typeof updates.metadata?.text === 'string'
          ? updates.metadata.text
          : originalAnnotation.text;

      const now = new Date();
      const utcNow = new Date(now.toUTCString());

      const clientAnnotation: Annotation = {
        ...updates,
        updatedAt: new Date(
          utcNow.toISOString().slice(0, 19).replace('T', ' '),
        ),
      };
      updateClientAnnotation(id, clientAnnotation);

      utils.documentAnnotation.all.setData(chatbotId, (oldData) => {
        if (!oldData) return { annotations: [] };

        return {
          annotations: oldData.annotations.map((ann) => {
            if (ann.id !== id) return ann;
            return {
              id: originalAnnotation.id,
              userId: originalAnnotation.userId,
              userDisplayName: originalAnnotation.userDisplayName,
              chatbotId: originalAnnotation.chatbotId,
              createdAt: originalAnnotation.createdAt,
              updatedAt: utcNow.toISOString().slice(0, 19).replace('T', ' '),
              pageNumber: updates.pageNumber,
              highlights: updates.highlights as HighlightRect[],
              color: updates.color || originalAnnotation.color,
              comment: updates.comment || null,
              text: originalAnnotation.text,
              borderColor:
                updates.borderColor || originalAnnotation.borderColor,
            };
          }),
        };
      });

      updateMutation.mutate({
        id,
        chatbotId,
        pageNumber: updates.pageNumber,
        highlights: updates.highlights,
        color: updates.color,
        borderColor: updates.borderColor,
        comment: updates.comment,
        text: updatedText,
      });
    },
    [
      chatbotId,
      updateMutation,
      updateClientAnnotation,
      utils,
      savedAnnotations,
    ],
  );

  const handleDeleteAnnotation = useCallback(
    (id: string) => {
      deleteMutation.mutate(id);
    },
    [deleteMutation],
  );

  const handleCopyContents = useCallback(
    async ({
      text,
      comment,
      chatbotId,
    }: { text: string; comment: string; chatbotId: string }) => {
      const citation = utils.citations.formatFromMetadata.getData({
        chatbotId,
      });
      const strippedCitation = citation ? citation.replace(/<[^>]+>/g, '') : '';
      await navigator.clipboard.writeText(
        `"${text}"${comment ? ` ${comment}` : ''}${strippedCitation ? ` ${strippedCitation}` : ''}`,
      );
    },
    [],
  );

  const handleAnnotationClick = useCallback((annotation: Annotation | null) => {
    setFocusedAnnotationId(annotation?.id);
  }, []);

  const handleAnnotationHover = useCallback((annotation: Annotation | null) => {
    if (annotation) {
      setFocusedHoverAnnotationId(annotation.id);
    } else {
      setFocusedHoverAnnotationId(undefined);
    }
  }, []);

  const handleCopyToChat = useCallback(
    (text: string, highlights: HighlightRect[], chatbotId: string) => {
      toggle('chat', true);
      addAttachments(
        [mapTextToAttachment(chatbotId, text, 'DOCUMENT', highlights)],
        chatbotId,
      );
    },
    [],
  );

  return {
    savedAnnotations: savedAnnotations?.annotations ?? [],
    isLoading,
    focusedAnnotationId,
    focusedHoverAnnotationId,
    handleCopyToChat,
    handleCreateAnnotation,
    handleUpdateAnnotation,
    handleDeleteAnnotation,
    handleAnnotationClick,
    handleCopyContents,
    handleAnnotationHover,
  };
};
